import * as React from 'react';
import logger from 'src/utils/logger';
import {SUPPORTED_LANGUAGES} from 'src/constants/language-support';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {
  LanguageSourceConfig,
  ExternalAppLanguageSourceConfig,
} from 'src/types/language-support';


export const isLanguageSupported = (language: string): boolean => {
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"en" | "fr-CA" | "es-419" | "it" | "fr" | "es" | "de" | "hu" | "ja" | "nl" | "pt" | "sv"'.
  return SUPPORTED_LANGUAGES.includes(language);
};

// using HOC to inject the `t` function into the class component
export function withI18n(Component: any) {
  return function WrapperComponent(props: any) {
    const {t, makeKey} = useLabelTranslation();
    return <Component {...props} t={t} makeKey={makeKey} />;
  };
}

// browser language with comprehensive error handling
export const getBrowserLanguage = (): string => {
  let detectedBrowserLanguage = '';
  try {
    // check if navigator is available (important for SSR pages)
    if (typeof navigator === 'undefined') {
      return detectedBrowserLanguage;
    }
    const browserLanguage = navigator.language || '';
    detectedBrowserLanguage =
      browserLanguage === 'en-US' ? 'en' : browserLanguage;
    return isLanguageSupported(detectedBrowserLanguage)
      ? detectedBrowserLanguage
      : '';
  } catch (error: any) {
    logger.error('Error detecting browser language:', error);
    return detectedBrowserLanguage;
  }
};

export const getLocalStorageSelectedLanguage = (): string => {
  let localStorageSelectedLanguage = '';
  try {
    // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'.
    localStorageSelectedLanguage = localStorage.getItem('selectedLanguage');
  } catch (e: any) {
    logger.error('Error getting selectedLanguage from localStorage', e);
  }
  return isLanguageSupported(localStorageSelectedLanguage || '')
    ? localStorageSelectedLanguage || ''
    : '';
};

export const getCurrentLanguageForExternalApps = (
  isLanguageSupportEnabled: boolean,
  externalAppConfig?: ExternalAppLanguageSourceConfig,
): string => {
  // @ts-expect-error - TS2339 - Property 'supportDefaultLanguage' does not exist on type 'ExternalAppLanguageSourceConfig'.
  const {supportBrowserLanguage = true, supportDefaultLanguage = true} =
    externalAppConfig || {};
  const selectedLanguageFromLocalStorage = supportDefaultLanguage
    ? getLocalStorageSelectedLanguage()
    : '';

  const browserDetectedLanguage = supportBrowserLanguage
    ? getBrowserLanguage()
    : '';
  return isLanguageSupportEnabled
    ? selectedLanguageFromLocalStorage || browserDetectedLanguage || ''
    : '';
};

export const getCurrentLanguageForApp = (
  isLanguageSupportEnabled: boolean,
  agentI18NSelectedLanguage: string,
  agencyDefaultLanguage: string,
  config?: LanguageSourceConfig,
): string => {
  const {
    supportBrowserLanguage = true,
    supportDefaultLanguage = true,
    supportLocalStorageLanguage = true,
    supportApiSelectedLanguage = true,
  } = config || {};

  const browserDetectedLanguage = supportBrowserLanguage
    ? getBrowserLanguage()
    : '';

  const selectedLanguageFromLocalStorage = supportLocalStorageLanguage
    ? getLocalStorageSelectedLanguage()
    : '';
  const agentSelectedLanguageFromApi =
    supportApiSelectedLanguage && agentI18NSelectedLanguage;

  const defaultLanguageFromAgencyConfig =
    supportDefaultLanguage && agencyDefaultLanguage;

  const finalResolvedLanguage = isLanguageSupportEnabled
    ? agentSelectedLanguageFromApi ||
      selectedLanguageFromLocalStorage ||
      defaultLanguageFromAgencyConfig ||
      browserDetectedLanguage ||
      'en'
    : 'en';

  return finalResolvedLanguage;
};
